
import { StoreApi } from 'zustand';
import { PriceState } from '../types';
import { UserState } from '../../auth/userSlice';
import { fetchMetalPrices, fetchMetalPriceHistory } from '../priceService';
import { MetalPrice, TimePeriod } from '@/lib/metal-price';
import { GoldPurity, SilverPurity, Unit, calculateGoldPrice, calculateSilverPrice } from '@/lib/pricing';

type SetPriceState = StoreApi<PriceState & UserState>['setState'];
type GetPriceState = StoreApi<PriceState & UserState>['getState'];

export const setSelectedTimePeriod = (
  set: SetPriceState,
  period: TimePeriod
) => {
  set({ selectedTimePeriod: period });
};

export const setGoldPurity = (set: SetPriceState, get: GetPriceState, purity: GoldPurity) => {
  const { currentGoldPrice } = get();
  const newGoldPrice = currentGoldPrice ? calculateGoldPrice(currentGoldPrice, purity) : 0;
  set({ goldPurity: purity, goldPrice: newGoldPrice });
};
export const setSilverPurity = (set: SetPriceState, get: GetPriceState, purity: SilverPurity) => {
  const { currentSilverPrice, silverUnit } = get();
  const newSilverPrice = currentSilverPrice ? calculateSilverPrice(currentSilverPrice, purity, silverUnit) : 0;
  set({ silverPurity: purity, silverPrice: newSilverPrice });
};
export const setSilverUnit = (set: SetPriceState, get: GetPriceState, unit: Unit) => {
  const { currentSilverPrice, silverPurity } = get();
  const newSilverPrice = currentSilverPrice ? calculateSilverPrice(currentSilverPrice, silverPurity, unit) : 0;
  set({ silverUnit: unit, silverPrice: newSilverPrice });
};

export const fetchCurrentPricesData = async (set: SetPriceState, get: GetPriceState): Promise<void> => {
  set({ isPriceLoading: true, error: null });
  try {
    const { gold, silver } = await fetchMetalPrices();
    
    const goldPurity = get().goldPurity;
    const silverPurity = get().silverPurity;
    const silverUnit = get().silverUnit;

    const newGoldPrice = gold ? calculateGoldPrice(gold, goldPurity) : 0;
    const newSilverPrice = silver ? calculateSilverPrice(silver, silverPurity, silverUnit) : 0;
    
    set({ 
      currentGoldPrice: gold,
      currentSilverPrice: silver,
      isPriceLoading: false,
      // backward compatibility
      rawGoldPrice: gold,
      rawSilverPrice: silver,
      goldPrice: newGoldPrice,
      silverPrice: newSilverPrice,
    });
  } catch (error: any) {
    console.error('Error in fetchCurrentPricesData action:', error);
    set({ isPriceLoading: false, error: error.message || 'Failed to fetch current prices' });
  }
};

export const fetchPriceHistoryData = async (
  set: SetPriceState,
  period: TimePeriod
): Promise<{ goldPriceHistory: Partial<MetalPrice>[]; silverPriceHistory: Partial<MetalPrice>[] }> => {
  set({ isHistoryLoading: true, error: null, goldPriceHistory: [], silverPriceHistory: [] });
  try {
    const { goldHistory, silverHistory } = await fetchMetalPriceHistory(period);
    
    set({ 
      goldPriceHistory: goldHistory,
      silverPriceHistory: silverHistory,
      isHistoryLoading: false,
    });
    
    return {
      goldPriceHistory: goldHistory,
      silverPriceHistory: silverHistory
    };
  } catch (error: any) {
    console.error('Error in fetchPriceHistoryData action:', error);
    set({ isHistoryLoading: false, error: error.message || 'Failed to fetch history' });
    return { goldPriceHistory: [], silverPriceHistory: [] };
  }
};
